import React, { useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { DatePicker } from "antd";
import moment from "moment";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";
import {
  FaCalendarAlt,
  FaChurch,
  FaBook,
  FaUser,
  FaUsers,
  FaComments,
  FaMoneyBillWave,
} from "react-icons/fa";

const UpdateWorshipChild = ({ worshipRecord }) => {
  let navigate = useNavigate();
  const [{ errorLogApi }] = useAtom(gbState);
  const [formData, setFormData] = useState({
    ...worshipRecord,
  });

  const handleChange = ({ currentTarget: input }) => {
    setFormData({
      ...formData,
      [input.name]: input.value,
    });
  };

  function formatString(str) {
    // Remove underscores and split the string into words
    const words = str.replace(/_/g, " ").split(" ");

    // Capitalize the first letter of the first word
    const capitalizedWords = words.map((word, index) => {
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    });

    // Join the words back into a single string
    return capitalizedWords.join(" ");
  }

  const handleWorshipRegister = () => {
    const fieldToValidate = [
      "worship_date",
      "worship_description",
      "nature_of_service",
      "reader",
      "celebrant_preacher",
      "text_subject",
      "no_of_communicants",
    ];

    // Identify empty fields and filter out any undefined entries
    const emptyFields = fieldToValidate
      .filter((f) => !formData[f])
      .map(formatString); // Assuming formatString formats the string as previously discussed

    // Provide feedback if there are any empty fields
    if (emptyFields.length > 0) {
      return toast.error(`${emptyFields.join(", ")} cannot be empty`);
    }

    let userToken = localStorage.getItem("token");
    const config = {
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/worship/update/${worshipRecord?._id}`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
      data: {
        ...formData,
      },
    };

    axios(config)
      .then(function (response) {
        toast.success(response?.data?.message);
        navigate("/worship/list");
      })
      .catch(function (error) {
        toast.error(
          error.response?.data
            ? JSON.stringify(error.response.data)
            : "An error occurred"
        );
        errorLogApi(error, "UpdateWorship", "/worship/update");
      });
  };

  return (
    <div className="max-w-4xl p-6 mx-auto bg-white rounded-lg shadow-lg">
      <h2 className="mb-6 text-2xl font-semibold text-gray-800">
        Update Worship Record
      </h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Worship Date
            </label>
            <DatePicker
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              name="worship_date"
              onChange={(date, dateString) =>
                setFormData({
                  ...formData,
                  worship_date: date ? date.format("YYYY-MM-DD") : "",
                })
              }
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
              value={
                formData?.worship_date
                  ? dayjs(formData.worship_date, "YYYY-MM-DD")
                  : null
              }
              format="MM-DD-YYYY"
              suffixIcon={<FaCalendarAlt className="text-gray-400" />}
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Worship Description
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="worship_description"
                type="text"
                required
                placeholder="Description"
                onChange={handleChange}
                value={formData.worship_description}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaChurch className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Nature of Service
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="nature_of_service"
                type="text"
                required
                placeholder="Nature of Service"
                onChange={handleChange}
                value={formData.nature_of_service}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaBook className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Reader
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="reader"
                type="text"
                required
                placeholder="Reader"
                onChange={handleChange}
                value={formData.reader}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaUser className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Celebrant Preacher
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="celebrant_preacher"
                type="text"
                required
                placeholder="Celebrant preacher"
                onChange={handleChange}
                value={formData.celebrant_preacher}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaUser className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              No of communicants
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="no_of_communicants"
                type="text"
                required
                placeholder="No of Communicants"
                onChange={handleChange}
                value={formData.no_of_communicants}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaUsers className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Offerings
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="offerings"
                type="text"
                required
                placeholder="Offerings"
                onChange={handleChange}
                value={formData.offerings}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaMoneyBillWave className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Text Subject
        </label>
        <div className="relative">
          <textarea
            className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            name="text_subject"
            rows={3}
            required
            placeholder="Text Subject"
            onChange={handleChange}
            value={formData.text_subject}
          />
          <div className="absolute pointer-events-none top-3 left-3">
            <FaBook className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>
      <div className="mt-6">
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Remarks
        </label>
        <div className="relative">
          <textarea
            className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            name="remarks"
            rows={3}
            placeholder="Additional remarks"
            onChange={handleChange}
            value={formData.remarks}
          />
          <div className="absolute pointer-events-none top-3 left-3">
            <FaComments className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-8 space-x-4">
        <button
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={handleWorshipRegister}
        >
          Update Record
        </button>
      </div>
    </div>
  );
};

export default UpdateWorshipChild;
